'use client';

import type { TrialOfferProps } from '@/core/types';

import React from 'react';

import { Lordicon } from '@/components/lordicon';
import { Box, Image, Button, Container, Typography } from '@/components/ui';

import { RouterLink } from '@/routes/components';

export default function TrialOfferV1({
  title,
  subtitle,
  image,
  items,
  buttons,
  className = '',
}: TrialOfferProps) {
  return (
    <div className={`${className} relative w-full text-white`}>
      {!!image?.url && (
        <Image
          src={image?.url || ''}
          alt={image?.alt || ''}
          aspectRatio="16/9"
          objectFit="fill"
          className="w-full absolute top-0 left-0 h-full object-cover -z-[1]"
          loading="eager"
          width={1440}
          height={800}
        />
      )}
      <Container className="flex flex-col items-center justify-center text-center py-[34px] md:py-[118px]">
        <Box className="w-full max-w-[970px]">
          {title && (
            <Typography
              variant="h2"
              className="text-center text-[24px] leading-[30px] md:text-[42px] md:leading-normal lg:text-[42px] lg:leading-[61px] font-bold mb-9 md:mb-0 uppercase"
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="body1"
              paragraph
              className="hidden md:block text-center text-[18px] leading-[22px] max-w-[832px] mx-auto mb-8 md:mb-[48px]"
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {items && items.length > 0 && (
          <Box className="flex flex-col md:flex-row justify-center gap-4 md:gap-10 w-full mb-8 md:mb-[48px]">
            {items.map((item) => (
              <Box
                key={item.id}
                className="flex flex-col items-center justify-center text-center min-h-[180px] md:min-h-[240px] w-full bg-white/80 rounded-2xl"
              >
                <Lordicon icon={item.icon} className="mb-3" size={58} trigger="loop" />
                <Typography
                  variant="body1"
                  paragraph
                  className="text-center font-medium text-lg text-black max-w-[270px]"
                >
                  <span dangerouslySetInnerHTML={{ __html: item.title }} />
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {buttons && buttons.length > 0 && (
          <Box className="flex justify-center gap-4">
            {buttons.map((button, index) => (
              <Button
                key={index}
                href={button.link}
                component={RouterLink}
                variant={button.variant}
                className={`min-w-[171px] md:min-w-[180px] px-2 md:px-4 text-sm md:text-base ${
                  index === 1 ? '!text-black' : ''
                } ${button.className}`}
              >
                {button.text}
              </Button>
            ))}
          </Box>
        )}

        <Box className="hidden md:flex items-center justify-center mt-12">
          <Image
            src="/assets/images/footer/wb-icon.webp"
            alt="WB"
            className="w-12 h-12 object-contain"
            loading="lazy"
            width={56}
            height={56}
          />
        </Box>
      </Container>
    </div>
  );
}
